import { Injectable, inject } from '@angular/core';
import { EventService } from '@shared/service/event.service';

interface ToasterEventModel extends Record<string, unknown> {
  header: string;
  payload: string;
}

@Injectable({
  providedIn: 'root'
})
export class ToasterBridgeService {
  private eventService = inject(EventService);
  private initialized = false;

  /**
   * Initialize bridge to intercept toaster messages from iframe
   * and display them in the parent Angular app's toaster
   */
  initializeBridge(iframeWindow: Window): void {
    if (this.initialized || !iframeWindow) {
      return;
    }

    // Add message listener to receive toaster events from iframe
    window.addEventListener('message', (event) => {
      // Verify the message is from our iframe
      if (event.source !== iframeWindow) {
        return;
      }

      // Check if it's a toaster message
      if (event.data?.type === 'iframe-toaster') {
        const { toastType, title, message } = event.data;
        const toasterData: ToasterEventModel = {
          header: title || '',
          payload: message || ''
        };

        // Display in parent window's toaster using EventService
        switch (toastType) {
          case 'error':
          case 'danger':
            this.eventService.emit('toast-error', toasterData);
            break;
          case 'success':
          case 'warning':
          case 'info':
          default:
            this.eventService.emit('toast', toasterData);
            break;
        }
      }
    });

    this.initialized = true;
  }

  /**
   * Inject toaster interceptor into iframe to catch all toaster calls
   * Uses external script to comply with CSP (no inline scripts)
   * @param iframeDoc - The iframe document to inject into
   * @param enabledPages - List of pages that should show toasts in parent (migrated pages)
   */
  injectToasterInterceptor(iframeDoc: Document, enabledPages: string[] = []): void {
    const scriptId = '__iframe_toaster_bridge__';

    // Check if already injected
    if (iframeDoc.getElementById(scriptId)) {
      return;
    }

    // Use external script to comply with CSP (no inline scripts)
    // Configuration is passed via data attributes
    const script = iframeDoc.createElement('script');
    script.type = 'text/javascript';
    script.id = scriptId;
    script.src = '/static/manager-studio/assets/js/iframe-bridge.js';
    script.setAttribute('data-enabled-pages', JSON.stringify(enabledPages));
    script.setAttribute('data-enable-url-monitor', 'false');
    script.setAttribute('data-enable-toaster', 'true');
    iframeDoc.head.appendChild(script);
  }
}
